import { useEffect, useState } from 'react';

/**
 * Chrome (since ~v142) asks before a public website may talk to a device on a
 * private network, and a Tailscale address counts as one. Until the person
 * answers the prompt, every request to the PlaneSight server simply hangs — it
 * looks like "Connecting…" forever. This notices that situation so the app can
 * say what's going on. Other browsers (Safari, Firefox) don't gate this, so
 * there the permission can't be queried and the hint never shows.
 */
export type LocalNetworkState = 'prompt' | 'denied';

const PERMISSION_NAMES = ['local-network-access', 'local-network'];
/** Give a slow connection time to open before suspecting a permission prompt. */
const WAIT_BEFORE_HINT_MS = 4_000;

async function queryState(): Promise<LocalNetworkState | null> {
  for (const name of PERMISSION_NAMES) {
    try {
      const status = await navigator.permissions.query({ name: name as PermissionName });
      return status.state === 'granted' ? null : status.state;
    } catch {
      /* this name isn't supported here */
    }
  }
  return null;
}

/** While `waiting` (not yet connected) for a few seconds, whether Chrome is holding the connection for a permission. */
export function useLocalNetworkHint(waiting: boolean): LocalNetworkState | null {
  const [state, setState] = useState<LocalNetworkState | null>(null);
  useEffect(() => {
    if (!waiting) {
      setState(null);
      return;
    }
    let cancelled = false;
    const timer = window.setTimeout(() => {
      void queryState().then((s) => {
        if (!cancelled) setState(s);
      });
    }, WAIT_BEFORE_HINT_MS);
    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [waiting]);
  return state;
}
